/**
 * Workflow Types for Tasking.tech Visual Workflow Builder
 * Advanced multi-step automation with browser, AI, and TaskingBot integration
 */

// Node types available in the workflow builder
export type WorkflowNodeType =
  // Triggers - Start a workflow
  | 'trigger_webhook'
  | 'trigger_schedule'
  | 'trigger_manual'
  // Browser Automation - Interact with websites via extension
  | 'browser_navigate'
  | 'browser_click'
  | 'browser_type'
  | 'browser_extract'
  | 'browser_screenshot'
  | 'browser_upload'
  | 'browser_wait'
  | 'browser_scroll'
  | 'browser_select'
  | 'browser_script'
  // AI - Generate content, images, decisions
  | 'ai_prompt'
  | 'ai_image_generate'
  | 'ai_image_edit'
  | 'ai_classify'
  | 'ai_extract'
  // Data - Transform and manage data between steps
  | 'data_transform'
  | 'data_store'
  | 'data_merge'
  | 'data_split'
  | 'data_filter'
  // Actions - External services and APIs
  | 'action_http'
  | 'action_code'
  | 'action_email'
  | 'action_slack'
  | 'action_discord'
  | 'action_database'
  // TaskingBot - Delegate complex tasks
  | 'taskingbot_execute'
  | 'taskingbot_create_task'
  // Logic - Control flow
  | 'logic_if'
  | 'logic_switch'
  | 'logic_loop'
  | 'logic_delay'
  | 'logic_parallel'
  | 'logic_retry'
  | 'logic_error_handler';

export interface Position {
  x: number;
  y: number;
}

export interface WorkflowNodeConfig {
  // Trigger configs
  webhookPath?: string;
  cronExpression?: string;

  // Browser automation configs
  url?: string;
  selector?: string;
  selectorType?: 'css' | 'xpath' | 'text' | 'aria-label' | 'ai-detect';
  text?: string;
  value?: string;
  waitTimeout?: number;
  waitCondition?: 'visible' | 'hidden' | 'exists' | 'url_contains' | 'text_contains';
  waitValue?: string;
  scrollDirection?: 'down' | 'up' | 'to_element' | 'to_bottom' | 'to_top';
  scrollAmount?: number;
  selectOption?: string;
  extractType?: 'text' | 'html' | 'attribute' | 'value' | 'table' | 'list' | 'screenshot';
  extractAttribute?: string;
  uploadSource?: 'previous_step' | 'url' | 'file_path' | 'generated';
  uploadFilePath?: string;
  uploadUrl?: string;
  browserScript?: string;
  useExtension?: boolean;
  useSavedCredentials?: boolean;
  fillCredentialFields?: { selector: string; credentialKey: string }[];

  // HTTP action config
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  headers?: Record<string, string> | string;
  body?: string;
  responseFormat?: 'json' | 'text' | 'binary';

  // Code action config
  code?: string;
  language?: 'javascript';

  // Email config
  to?: string;
  subject?: string;
  bodyTemplate?: string;

  // Slack/Discord config
  webhookUrl?: string;
  message?: string;

  // AI config
  prompt?: string;
  model?: string;
  systemPrompt?: string;
  temperature?: number;
  maxTokens?: number;

  // AI Image config
  imagePrompt?: string;
  imageStyle?: string;
  imageSize?: '1024x1024' | '1536x1024' | '1024x1536' | '512x512';
  imageProvider?: 'openai' | 'gemini' | 'flux';
  imageInputSource?: 'previous_step' | 'url';
  imageInputUrl?: string;
  editInstructions?: string;

  // AI Classify/Extract config
  categories?: string[];
  extractSchema?: string;
  extractFields?: string[];

  // Data transform config
  transformExpression?: string;
  transformType?: 'map' | 'filter' | 'reduce' | 'pick' | 'rename' | 'template' | 'custom';
  templateString?: string;
  pickFields?: string[];
  renameMap?: Record<string, string>;

  // Data store config
  variableName?: string;
  storeOperation?: 'set' | 'append' | 'increment' | 'merge';

  // Data merge/split
  mergeStrategy?: 'shallow' | 'deep' | 'concat_arrays';
  splitField?: string;
  splitDelimiter?: string;

  // Data filter
  filterExpression?: string;

  // Database config
  operation?: 'query' | 'insert' | 'update' | 'delete';
  table?: string;
  query?: string;

  // Logic configs
  condition?: string;
  cases?: { value: string; label: string }[];
  loopCount?: number;
  loopVariable?: string;
  loopOver?: string; // expression to iterate
  delayMs?: number;
  retryCount?: number;
  retryDelay?: number;
  retryCondition?: string;
  errorAction?: 'continue' | 'retry' | 'stop' | 'fallback';
  fallbackValue?: string;

  // TaskingBot configs
  taskTitle?: string;
  taskDescription?: string;
  taskPriority?: 'low' | 'medium' | 'high' | 'urgent';
  taskInstructions?: string;
  waitForCompletion?: boolean;

  // General
  description?: string; // Node-level annotation
  outputVariable?: string; // Name this node's output for reference
}

export interface WorkflowNode {
  id: string;
  type: WorkflowNodeType;
  name: string;
  position: Position;
  config: WorkflowNodeConfig;
}

export interface WorkflowConnection {
  id: string;
  sourceNodeId: string;
  sourceHandle: string; // 'output' or 'true' / 'false' for conditionals
  targetNodeId: string;
  targetHandle: string; // 'input'
}

export interface WorkflowDefinition {
  id: string;
  name: string;
  description?: string;
  nodes: WorkflowNode[];
  connections: WorkflowConnection[];
  variables?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

// Execution types
export type ExecutionStatus = 'pending' | 'running' | 'success' | 'error' | 'cancelled';

export interface NodeExecutionResult {
  nodeId: string;
  status: ExecutionStatus;
  output?: any;
  error?: string;
  startedAt: string;
  completedAt?: string;
  durationMs?: number;
}

export interface WorkflowExecution {
  id: string;
  workflowId: string;
  status: ExecutionStatus;
  triggerType: 'webhook' | 'schedule' | 'manual';
  triggerData?: any;
  nodeResults: NodeExecutionResult[];
  startedAt: string;
  completedAt?: string;
  error?: string;
}

// Node definitions for the UI
export interface NodeDefinition {
  type: WorkflowNodeType;
  category: 'trigger' | 'browser' | 'ai' | 'data' | 'action' | 'taskingbot' | 'logic';
  name: string;
  description: string;
  icon: string;
  color: string;
  inputs: number;
  outputs: number;
  outputLabels?: string[];
  configFields: ConfigField[];
}

export interface ConfigField {
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'select' | 'number' | 'code' | 'json' | 'checkbox' | 'credential-map';
  placeholder?: string;
  options?: { value: string; label: string }[];
  required?: boolean;
  defaultValue?: any;
  helpText?: string;
}

// All available node definitions
export const NODE_DEFINITIONS: NodeDefinition[] = [
  // ─── TRIGGERS ───────────────────────────────────────────
  {
    type: 'trigger_webhook',
    category: 'trigger',
    name: 'Webhook',
    description: 'Trigger via HTTP request',
    icon: 'Webhook',
    color: '#10b981',
    inputs: 0,
    outputs: 1,
    configFields: [
      { key: 'webhookPath', label: 'Path', type: 'text', placeholder: '/my-webhook', required: true },
    ],
  },
  {
    type: 'trigger_schedule',
    category: 'trigger',
    name: 'Schedule',
    description: 'Trigger on a cron schedule',
    icon: 'Clock',
    color: '#10b981',
    inputs: 0,
    outputs: 1,
    configFields: [
      { key: 'cronExpression', label: 'Cron Expression', type: 'text', placeholder: '0 * * * *', required: true },
    ],
  },
  {
    type: 'trigger_manual',
    category: 'trigger',
    name: 'Manual',
    description: 'Trigger manually from UI',
    icon: 'Play',
    color: '#10b981',
    inputs: 0,
    outputs: 1,
    configFields: [],
  },

  // ─── BROWSER AUTOMATION ─────────────────────────────────
  {
    type: 'browser_navigate',
    category: 'browser',
    name: 'Navigate',
    description: 'Open a URL in browser',
    icon: 'Globe',
    color: '#8b5cf6',
    inputs: 1,
    outputs: 1,
    configFields: [
      { key: 'url', label: 'URL', type: 'text', placeholder: 'https://example.com/{{input.path}}', required: true },
      { key: 'waitTimeout', label: 'Wait for page load (ms)', type: 'number', defaultValue: 5000 },
      { key: 'useExtension', label: 'Use browser extension', type: 'checkbox', defaultValue: true, helpText: 'Execute via TaskingBot browser extension (required for authenticated sites)' },
    ],
  },
  {
    type: 'browser_click',
    category: 'browser',
    name: 'Click',
    description: 'Click an element on page',
    icon: 'MousePointerClick',
    color: '#8b5cf6',
    inputs: 1,
    outputs: 1,
    configFields: [
      { key: 'selector', label: 'Element selector', type: 'text', placeholder: 'button.submit, #login-btn, "Sign In"', required: true },
      { key: 'selectorType', label: 'Selector type', type: 'select', options: [
        { value: 'css', label: 'CSS Selector' },
        { value: 'xpath', label: 'XPath' },
        { value: 'text', label: 'Text content' },
        { value: 'aria-label', label: 'Aria label' },
        { value: 'ai-detect', label: 'AI auto-detect (describe element)' },
      ], defaultValue: 'css' },
      { key: 'waitTimeout', label: 'Wait for element (ms)', type: 'number', defaultValue: 5000 },
    ],
  },
  {
    type: 'browser_type',
    category: 'browser',
    name: 'Type Text',
    description: 'Type text into an input field',
    icon: 'Keyboard',
    color: '#8b5cf6',
    inputs: 1,
    outputs: 1,
    configFields: [
      { key: 'selector', label: 'Input selector', type: 'text', placeholder: 'input[name="email"]', required: true },
      { key: 'selectorType', label: 'Selector type', type: 'select', options: [
        { value: 'css', label: 'CSS Selector' },
        { value: 'xpath', label: 'XPath' },
        { value: 'text', label: 'Placeholder text' },
        { value: 'aria-label', label: 'Aria label' },
        { value: 'ai-detect', label: 'AI auto-detect' },
      ], defaultValue: 'css' },
      { key: 'text', label: 'Text to type', type: 'textarea', placeholder: '{{input.email}} or literal text', required: true },
      { key: 'useSavedCredentials', label: 'Use saved credentials if available', type: 'checkbox', defaultValue: false, helpText: 'Check browser/extension saved passwords first before typing' },
    ],
  },
  {
    type: 'browser_extract',
    category: 'browser',
    name: 'Extract Data',
    description: 'Extract text, HTML, or data from page',
    icon: 'FileSearch',
    color: '#8b5cf6',
    inputs: 1,
    outputs: 1,
    configFields: [
      { key: 'selector', label: 'Element selector', type: 'text', placeholder: '.product-price, table.results', required: true },
      { key: 'extractType', label: 'Extract as', type: 'select', options: [
        { value: 'text', label: 'Text content' },
        { value: 'html', label: 'Inner HTML' },
        { value: 'attribute', label: 'Attribute value' },
        { value: 'value', label: 'Input value' },
        { value: 'table', label: 'Table → JSON array' },
        { value: 'list', label: 'List → JSON array' },
      ], defaultValue: 'text' },
      { key: 'extractAttribute', label: 'Attribute name', type: 'text', placeholder: 'href, src, data-id' },
    ],
  },
  {
    type: 'browser_screenshot',
    category: 'browser',
    name: 'Screenshot',
    description: 'Capture screenshot of page or element',
    icon: 'Camera',
    color: '#8b5cf6',
    inputs: 1,
    outputs: 1,
    configFields: [
      { key: 'selector', label: 'Element (empty = full page)', type: 'text', placeholder: '.main-content' },
      { key: 'outputVariable', label: 'Save as variable', type: 'text', placeholder: 'screenshot_url' },
    ],
  },
  {
    type: 'browser_upload',
    category: 'browser',
    name: 'Upload File',
    description: 'Upload a file to a website input',
    icon: 'Upload',
    color: '#8b5cf6',
    inputs: 1,
    outputs: 1,
    configFields: [
      { key: 'selector', label: 'File input selector', type: 'text', placeholder: 'input[type="file"]', required: true },
      { key: 'uploadSource', label: 'File source', type: 'select', options: [
        { value: 'previous_step', label: 'From previous step output' },
        { value: 'url', label: 'Download from URL' },
        { value: 'generated', label: 'AI-generated image from earlier step' },
      ], defaultValue: 'previous_step' },
      { key: 'uploadUrl', label: 'File URL (if source = URL)', type: 'text', placeholder: 'https://... or {{input.imageUrl}}' },
      { key: 'waitTimeout', label: 'Wait after upload (ms)', type: 'number', defaultValue: 3000 },
    ],
  },
  {
    type: 'browser_wait',
    category: 'browser',
    name: 'Wait For',
    description: 'Wait for element or condition',
    icon: 'Hourglass',
    color: '#8b5cf6',
    inputs: 1,
    outputs: 1,
    configFields: [
      { key: 'waitCondition', label: 'Wait until', type: 'select', options: [
        { value: 'visible', label: 'Element is visible' },
        { value: 'hidden', label: 'Element disappears' },
        { value: 'exists', label: 'Element exists in DOM' },
        { value: 'url_contains', label: 'URL contains text' },
        { value: 'text_contains', label: 'Page contains text' },
      ], defaultValue: 'visible', required: true },
      { key: 'selector', label: 'Selector / Text', type: 'text', placeholder: '.success-message, "Upload complete"', required: true },
      { key: 'waitTimeout', label: 'Timeout (ms)', type: 'number', defaultValue: 10000 },
    ],
  },
  {
    type: 'browser_scroll',
    category: 'browser',
    name: 'Scroll',
    description: 'Scroll page or to element',
    icon: 'ArrowDownUp',
    color: '#8b5cf6',
    inputs: 1,
    outputs: 1,
    configFields: [
      { key: 'scrollDirection', label: 'Scroll to', type: 'select', options: [
        { value: 'down', label: 'Down by amount' },
        { value: 'up', label: 'Up by amount' },
        { value: 'to_element', label: 'Scroll to element' },
        { value: 'to_bottom', label: 'Page bottom' },
        { value: 'to_top', label: 'Page top' },
      ], defaultValue: 'down' },
      { key: 'selector', label: 'Target element (if scroll to element)', type: 'text' },
      { key: 'scrollAmount', label: 'Pixels (if scroll by amount)', type: 'number', defaultValue: 500 },
    ],
  },
  {
    type: 'browser_select',
    category: 'browser',
    name: 'Select Option',
    description: 'Select dropdown option',
    icon: 'ListFilter',
    color: '#8b5cf6',
    inputs: 1,
    outputs: 1,
    configFields: [
      { key: 'selector', label: 'Select element', type: 'text', placeholder: 'select#category', required: true },
      { key: 'selectOption', label: 'Option value or text', type: 'text', placeholder: 'digital-art or "Digital Art"', required: true },
    ],
  },
  {
    type: 'browser_script',
    category: 'browser',
    name: 'Run Script',
    description: 'Execute JavaScript in browser context',
    icon: 'Terminal',
    color: '#8b5cf6',
    inputs: 1,
    outputs: 1,
    configFields: [
      { key: 'browserScript', label: 'JavaScript', type: 'code', placeholder: '// Runs in page context\nreturn document.title;', required: true },
    ],
  },

  // ─── AI ─────────────────────────────────────────────────
  {
    type: 'ai_prompt',
    category: 'ai',
    name: 'AI Prompt',
    description: 'Generate text with AI',
    icon: 'Sparkles',
    color: '#ec4899',
    inputs: 1,
    outputs: 1,
    configFields: [
      { key: 'prompt', label: 'Prompt', type: 'textarea', placeholder: 'Write a compelling title for: {{input.data}}', required: true },
      { key: 'systemPrompt', label: 'System prompt (optional)', type: 'textarea', placeholder: 'You are an expert copywriter...' },
      { key: 'model', label: 'Model', type: 'select', options: [
        { value: 'qwen3-coder-next:cloud', label: 'Qwen 3 Coder (Fast)' },
        { value: 'llama3.3:70b', label: 'Llama 3.3 70B' },
        { value: 'deepseek-r1:70b', label: 'DeepSeek R1 70B' },
      ], defaultValue: 'qwen3-coder-next:cloud' },
      { key: 'temperature', label: 'Temperature', type: 'number', defaultValue: 0.7, helpText: '0 = deterministic, 1 = creative' },
      { key: 'maxTokens', label: 'Max tokens', type: 'number', defaultValue: 2000 },
    ],
  },
  {
    type: 'ai_image_generate',
    category: 'ai',
    name: 'Generate Image',
    description: 'Create image from text prompt',
    icon: 'ImagePlus',
    color: '#ec4899',
    inputs: 1,
    outputs: 1,
    configFields: [
      { key: 'imagePrompt', label: 'Image prompt', type: 'textarea', placeholder: 'A futuristic city at sunset, digital art style', required: true },
      { key: 'imageStyle', label: 'Style modifier', type: 'text', placeholder: 'photorealistic, anime, watercolor, 3d render' },
      { key: 'imageSize', label: 'Size', type: 'select', options: [
        { value: '1024x1024', label: '1024×1024 (Square)' },
        { value: '1536x1024', label: '1536×1024 (Landscape)' },
        { value: '1024x1536', label: '1024×1536 (Portrait)' },
      ], defaultValue: '1024x1024' },
      { key: 'imageProvider', label: 'Provider', type: 'select', options: [
        { value: 'openai', label: 'OpenAI DALL-E 3' },
        { value: 'gemini', label: 'Google Gemini' },
        { value: 'flux', label: 'Flux' },
      ], defaultValue: 'openai' },
      { key: 'outputVariable', label: 'Save URL as', type: 'text', placeholder: 'generated_image_url' },
    ],
  },
  {
    type: 'ai_image_edit',
    category: 'ai',
    name: 'Edit Image',
    description: 'Modify an existing image with AI',
    icon: 'Paintbrush',
    color: '#ec4899',
    inputs: 1,
    outputs: 1,
    configFields: [
      { key: 'imageInputSource', label: 'Source image', type: 'select', options: [
        { value: 'previous_step', label: 'From previous step' },
        { value: 'url', label: 'From URL' },
      ], defaultValue: 'previous_step' },
      { key: 'imageInputUrl', label: 'Image URL (if source = URL)', type: 'text', placeholder: 'https://...' },
      { key: 'editInstructions', label: 'Edit instructions', type: 'textarea', placeholder: 'Remove the background, add a logo in the corner', required: true },
    ],
  },
  {
    type: 'ai_classify',
    category: 'ai',
    name: 'AI Classify',
    description: 'Classify input into categories',
    icon: 'Tags',
    color: '#ec4899',
    inputs: 1,
    outputs: 2,
    outputLabels: ['Matched', 'No Match'],
    configFields: [
      { key: 'prompt', label: 'What to classify', type: 'textarea', placeholder: '{{input.text}}', required: true },
      { key: 'categories', label: 'Categories (one per line)', type: 'textarea', placeholder: 'positive\nnegative\nneutral', required: true },
    ],
  },
  {
    type: 'ai_extract',
    category: 'ai',
    name: 'AI Extract',
    description: 'Extract structured data from text',
    icon: 'FileSearch',
    color: '#ec4899',
    inputs: 1,
    outputs: 1,
    configFields: [
      { key: 'prompt', label: 'Text to extract from', type: 'textarea', placeholder: '{{input.pageContent}}', required: true },
      { key: 'extractSchema', label: 'Fields to extract (JSON schema)', type: 'json', placeholder: '{"name": "string", "price": "number", "features": "string[]"}', required: true },
    ],
  },

  // ─── DATA ───────────────────────────────────────────────
  {
    type: 'data_transform',
    category: 'data',
    name: 'Transform',
    description: 'Transform data between steps',
    icon: 'Shuffle',
    color: '#06b6d4',
    inputs: 1,
    outputs: 1,
    configFields: [
      { key: 'transformType', label: 'Transform type', type: 'select', options: [
        { value: 'template', label: 'Template string' },
        { value: 'pick', label: 'Pick fields' },
        { value: 'rename', label: 'Rename fields' },
        { value: 'custom', label: 'Custom JavaScript' },
      ], defaultValue: 'template' },
      { key: 'templateString', label: 'Template (if template type)', type: 'textarea', placeholder: 'Title: {{input.title}}\nPrice: ${{input.price}}' },
      { key: 'pickFields', label: 'Fields to keep (if pick)', type: 'text', placeholder: 'name, email, phone' },
      { key: 'transformExpression', label: 'JS expression (if custom)', type: 'code', placeholder: '// return transformed data\nreturn { ...input, formatted: input.name.toUpperCase() };' },
    ],
  },
  {
    type: 'data_store',
    category: 'data',
    name: 'Store Variable',
    description: 'Save data for use in later steps',
    icon: 'Database',
    color: '#06b6d4',
    inputs: 1,
    outputs: 1,
    configFields: [
      { key: 'variableName', label: 'Variable name', type: 'text', placeholder: 'my_data', required: true },
      { key: 'storeOperation', label: 'Operation', type: 'select', options: [
        { value: 'set', label: 'Set (overwrite)' },
        { value: 'append', label: 'Append to array' },
        { value: 'increment', label: 'Increment number' },
        { value: 'merge', label: 'Merge objects' },
      ], defaultValue: 'set' },
      { key: 'value', label: 'Value (optional, default = input)', type: 'text', placeholder: '{{input.result}}' },
    ],
  },
  {
    type: 'data_merge',
    category: 'data',
    name: 'Merge',
    description: 'Merge data from multiple inputs',
    icon: 'Merge',
    color: '#06b6d4',
    inputs: 1,
    outputs: 1,
    configFields: [
      { key: 'mergeStrategy', label: 'Strategy', type: 'select', options: [
        { value: 'shallow', label: 'Shallow merge' },
        { value: 'deep', label: 'Deep merge' },
        { value: 'concat_arrays', label: 'Concat arrays' },
      ], defaultValue: 'shallow' },
    ],
  },
  {
    type: 'data_filter',
    category: 'data',
    name: 'Filter',
    description: 'Filter array or check condition',
    icon: 'Filter',
    color: '#06b6d4',
    inputs: 1,
    outputs: 2,
    outputLabels: ['Pass', 'Reject'],
    configFields: [
      { key: 'filterExpression', label: 'Filter (JS expression)', type: 'code', placeholder: '// For arrays: item.price > 10\n// For objects: input.status === "active"', required: true },
    ],
  },

  // ─── ACTIONS ────────────────────────────────────────────
  {
    type: 'action_http',
    category: 'action',
    name: 'HTTP Request',
    description: 'Make an HTTP API request',
    icon: 'Globe',
    color: '#3b82f6',
    inputs: 1,
    outputs: 1,
    configFields: [
      { key: 'method', label: 'Method', type: 'select', options: [
        { value: 'GET', label: 'GET' },
        { value: 'POST', label: 'POST' },
        { value: 'PUT', label: 'PUT' },
        { value: 'PATCH', label: 'PATCH' },
        { value: 'DELETE', label: 'DELETE' },
      ], defaultValue: 'GET' },
      { key: 'url', label: 'URL', type: 'text', placeholder: 'https://api.example.com/{{input.id}}', required: true },
      { key: 'headers', label: 'Headers (JSON)', type: 'json', placeholder: '{"Authorization": "Bearer ..."}' },
      { key: 'body', label: 'Body', type: 'textarea', placeholder: '{"data": "{{input.value}}"}' },
      { key: 'responseFormat', label: 'Response format', type: 'select', options: [
        { value: 'json', label: 'JSON' },
        { value: 'text', label: 'Text' },
        { value: 'binary', label: 'Binary (file)' },
      ], defaultValue: 'json' },
    ],
  },
  {
    type: 'action_code',
    category: 'action',
    name: 'Run Code',
    description: 'Execute JavaScript code',
    icon: 'Code',
    color: '#3b82f6',
    inputs: 1,
    outputs: 1,
    configFields: [
      { key: 'code', label: 'JavaScript Code', type: 'code', placeholder: '// Access input via `input`, all vars via `variables`\nreturn { result: input.data * 2 };', required: true },
    ],
  },
  {
    type: 'action_slack',
    category: 'action',
    name: 'Slack Message',
    description: 'Send a Slack message',
    icon: 'MessageSquare',
    color: '#3b82f6',
    inputs: 1,
    outputs: 1,
    configFields: [
      { key: 'webhookUrl', label: 'Webhook URL', type: 'text', placeholder: 'https://hooks.slack.com/...', required: true },
      { key: 'message', label: 'Message', type: 'textarea', placeholder: 'Result: {{input.data}}', required: true },
    ],
  },
  {
    type: 'action_discord',
    category: 'action',
    name: 'Discord Message',
    description: 'Send a Discord message',
    icon: 'MessageCircle',
    color: '#3b82f6',
    inputs: 1,
    outputs: 1,
    configFields: [
      { key: 'webhookUrl', label: 'Webhook URL', type: 'text', placeholder: 'https://discord.com/api/webhooks/...', required: true },
      { key: 'message', label: 'Message', type: 'textarea', placeholder: 'Alert: {{input.data}}', required: true },
    ],
  },

  // ─── TASKINGBOT ─────────────────────────────────────────
  {
    type: 'taskingbot_execute',
    category: 'taskingbot',
    name: 'TaskingBot Execute',
    description: 'Have TaskingBot perform a complex task',
    icon: 'Bot',
    color: '#f97316',
    inputs: 1,
    outputs: 1,
    configFields: [
      { key: 'taskInstructions', label: 'Instructions for TaskingBot', type: 'textarea', placeholder: 'Navigate to {{input.url}} and fill out the form with the data from the previous step. Upload {{variables.generated_image}} to the file field.', required: true },
      { key: 'useExtension', label: 'Use browser extension', type: 'checkbox', defaultValue: true },
      { key: 'waitForCompletion', label: 'Wait for completion', type: 'checkbox', defaultValue: true },
      { key: 'waitTimeout', label: 'Max wait time (ms)', type: 'number', defaultValue: 120000 },
    ],
  },
  {
    type: 'taskingbot_create_task',
    category: 'taskingbot',
    name: 'Create Task',
    description: 'Create a TaskingBot task for later execution',
    icon: 'ClipboardPlus',
    color: '#f97316',
    inputs: 1,
    outputs: 1,
    configFields: [
      { key: 'taskTitle', label: 'Task title', type: 'text', placeholder: 'Upload images to {{input.siteName}}', required: true },
      { key: 'taskDescription', label: 'Description', type: 'textarea', placeholder: 'Full task instructions...' },
      { key: 'taskPriority', label: 'Priority', type: 'select', options: [
        { value: 'low', label: 'Low' },
        { value: 'medium', label: 'Medium' },
        { value: 'high', label: 'High' },
        { value: 'urgent', label: 'Urgent' },
      ], defaultValue: 'medium' },
    ],
  },

  // ─── LOGIC ──────────────────────────────────────────────
  {
    type: 'logic_if',
    category: 'logic',
    name: 'If / Else',
    description: 'Branch based on condition',
    icon: 'GitBranch',
    color: '#f59e0b',
    inputs: 1,
    outputs: 2,
    outputLabels: ['True', 'False'],
    configFields: [
      { key: 'condition', label: 'Condition (JavaScript)', type: 'text', placeholder: 'input.status === "success" && input.count > 0', required: true },
    ],
  },
  {
    type: 'logic_switch',
    category: 'logic',
    name: 'Switch',
    description: 'Branch to multiple paths',
    icon: 'Network',
    color: '#f59e0b',
    inputs: 1,
    outputs: 4,
    outputLabels: ['Case 1', 'Case 2', 'Case 3', 'Default'],
    configFields: [
      { key: 'condition', label: 'Value to check', type: 'text', placeholder: 'input.category', required: true },
      { key: 'cases', label: 'Cases (JSON)', type: 'json', placeholder: '[{"value": "art", "label": "Art"}, {"value": "photo", "label": "Photo"}]' },
    ],
  },
  {
    type: 'logic_loop',
    category: 'logic',
    name: 'Loop',
    description: 'Iterate over array or repeat N times',
    icon: 'Repeat',
    color: '#f59e0b',
    inputs: 1,
    outputs: 2,
    outputLabels: ['Each Item', 'Done'],
    configFields: [
      { key: 'loopOver', label: 'Array to iterate', type: 'text', placeholder: 'input.items or input.urls', required: true },
      { key: 'loopVariable', label: 'Item variable name', type: 'text', placeholder: 'item', defaultValue: 'item' },
      { key: 'loopCount', label: 'Max iterations (0 = unlimited)', type: 'number', defaultValue: 0 },
    ],
  },
  {
    type: 'logic_delay',
    category: 'logic',
    name: 'Delay',
    description: 'Wait before continuing',
    icon: 'Timer',
    color: '#f59e0b',
    inputs: 1,
    outputs: 1,
    configFields: [
      { key: 'delayMs', label: 'Delay (ms)', type: 'number', placeholder: '1000', defaultValue: 1000 },
    ],
  },
  {
    type: 'logic_retry',
    category: 'logic',
    name: 'Retry',
    description: 'Retry previous step on failure',
    icon: 'RotateCcw',
    color: '#f59e0b',
    inputs: 1,
    outputs: 2,
    outputLabels: ['Success', 'All Failed'],
    configFields: [
      { key: 'retryCount', label: 'Max retries', type: 'number', defaultValue: 3 },
      { key: 'retryDelay', label: 'Delay between retries (ms)', type: 'number', defaultValue: 2000 },
      { key: 'retryCondition', label: 'Retry if (JS expression)', type: 'text', placeholder: 'input.status !== "success"' },
    ],
  },
  {
    type: 'logic_error_handler',
    category: 'logic',
    name: 'Error Handler',
    description: 'Catch errors and continue or fallback',
    icon: 'ShieldAlert',
    color: '#f59e0b',
    inputs: 1,
    outputs: 2,
    outputLabels: ['Success', 'Error'],
    configFields: [
      { key: 'errorAction', label: 'On error', type: 'select', options: [
        { value: 'continue', label: 'Continue with null' },
        { value: 'retry', label: 'Retry once' },
        { value: 'fallback', label: 'Use fallback value' },
        { value: 'stop', label: 'Stop workflow' },
      ], defaultValue: 'continue' },
      { key: 'fallbackValue', label: 'Fallback value (JSON)', type: 'json', placeholder: '{"status": "skipped"}' },
    ],
  },
];

// Helper to get node definition
export function getNodeDefinition(type: WorkflowNodeType): NodeDefinition | undefined {
  return NODE_DEFINITIONS.find((def) => def.type === type);
}

// Helper to create a new node
export function createNode(type: WorkflowNodeType, position: Position): WorkflowNode {
  const def = getNodeDefinition(type);
  return {
    id: crypto.randomUUID(),
    type,
    name: def?.name || type,
    position,
    config: {},
  };
}
